import { useEffect, useLayoutEffect, useSyncExternalStore, useRef } from "react";
import type { OrchestrationThreadActivity } from "@cleo/contracts";
import { randomUUID } from "../../lib/utils";
import { fetchPrimary } from "../../environments/primary/fetch";
import { Button } from "../ui/button";
import {
  LiveTalkController,
  LiveStartNotDispatched,
  type LiveDependencies,
  type LiveRequestOperation,
  LIVE_LIMIT_CHOICES,
  LIVE_DEFAULT_VOICE,
  liveVoiceChoices,
  type LiveScope,
  type LiveState,
  type LiveAgentReference,
  validLiveAgentReference,
  type LivePrivacy,
  validLiveProviderStorage,
} from "./liveTalkController";
import { createLiveMedia } from "./liveTalkMedia";
import { useLiveVoicePresentation, type LivePresentationRead } from "./useLiveVoicePresentation";

const PRIVACY_OPERATIONS: ReadonlySet<string> = new Set(["privacy", "privacy-set", "voice-set"]);
export function requestLiveVoice(
  operation: "presentation" | "open",
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<LivePresentationRead>;
export function requestLiveVoice(
  operation: LiveRequestOperation,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): ReturnType<LiveDependencies["request"]>;
export async function requestLiveVoice(
  operation: LiveRequestOperation | "presentation" | "open",
  body: Record<string, unknown>,
  signal?: AbortSignal,
) {
  const path = PRIVACY_OPERATIONS.has(operation)
    ? `/api/neucleos/live/${operation}`
    : `/api/neucleos/voice/live/${operation}`;
  const response = await fetchPrimary(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal: signal
      ? AbortSignal.any([signal, AbortSignal.timeout(30_000)])
      : AbortSignal.timeout(30_000),
  });
  // A door that is absent or answers prose is a refusal to display, never a retry.
  const result = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok || result.ok !== true) {
    const reason =
      typeof result.reason === "string"
        ? result.reason
        : typeof result.error === "string"
          ? result.error
          : `Live is unavailable for this conversation (HTTP ${response.status}).`;
    if (
      operation === "start" &&
      !response.ok &&
      result.ok === false &&
      result.admission === "not-dispatched" &&
      typeof body.requestKey === "string"
    )
      throw new LiveStartNotDispatched(reason, body.requestKey);
    throw new Error(reason);
  }
  // The overloads name the shape each caller may rely on; the door returns JSON.
  return result as never;
}

const readLivePresentation = (body: Record<string, unknown>, signal: AbortSignal) =>
  requestLiveVoice("presentation", body, signal);

let controller: LiveTalkController | undefined;
/** LVC1: the transcript hook registers how a dropped call's rows are saved before a replacement starts. */
let transcriptFlusher: ((requestKey: string) => Promise<boolean>) | null = null;
export function setLiveTranscriptFlusher(
  flusher: ((requestKey: string) => Promise<boolean>) | null,
) {
  transcriptFlusher = flusher;
}
/** The Desk's platform pieces for the shared controller: browser time, timers, ids, WebRTC media, loopback door. */
function sharedController() {
  return (controller ??= new LiveTalkController({
    clock: {
      now: () => Date.now(),
      setTimeout: (callback, ms) => setTimeout(callback, ms),
      clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    },
    uuid: randomUUID,
    media: createLiveMedia,
    request: requestLiveVoice,
    flushTranscript: (requestKey) =>
      transcriptFlusher ? transcriptFlusher(requestKey) : Promise.resolve(false),
  }));
}
/**
 * Sign-out and World switch End an admitted call through the stop door while
 * the grant that admitted it still exists, waiting (bounded) for the stop
 * round trip. "idle" when nothing was running; "unconfirmed" when the bound
 * elapsed first, in which case the World's observer reconciles the call.
 */
export function endLiveTalkForSignOut(boundMs = 8_000): Promise<"idle" | "ended" | "unconfirmed"> {
  return controller ? controller.endAndSettle(boundMs) : Promise.resolve("idle");
}
export const ownLiveAgentCall = (worldKey: string, actorId: string, agentKey: string) =>
  controller?.ownAgentCall(worldKey, actorId, agentKey) ?? null;
export function endOwnLiveAgentCall(
  worldKey: string,
  actorId: string,
  agentKey: string,
  requestKey: string,
) {
  if (controller?.ownAgentCall(worldKey, actorId, agentKey)?.requestKey !== requestKey)
    throw new Error("That Live call is no longer owned here. Review its current controls.");
  controller.end();
}
export function requireLiveAgentTestReviewAvailable(
  address: Pick<LiveScope, "worldKey" | "expectedActorId" | "conversation">,
) {
  if (!controller?.canReview(address))
    throw new Error(
      "Live must be available in this conversation with no active call before reviewing an agent.",
    );
}

/** Explicit UI request through the same World presentation door as Cleo. Never starts media. */
export async function requestLiveAgentTestReview(
  address: Pick<LiveScope, "worldKey" | "expectedActorId" | "conversation">,
  agent: LiveAgentReference,
  isCurrent: () => boolean,
) {
  const owner = controller;
  if (!owner?.canReview(address) || !isCurrent() || !validLiveAgentReference(agent))
    throw new Error(
      "Open this conversation's Live controls when no call is active, then review the saved agent.",
    );
  const requestKey = randomUUID();
  const result = await requestLiveVoice("open", { ...address, agent, requestKey });
  if (!isCurrent() || !owner.canReview(address))
    throw new Error(
      "The conversation or signed-in person changed before the voice agent review opened.",
    );
  const intent = result.presentation;
  const now = Date.now();
  if (
    result.worldKey !== address.worldKey ||
    result.actorId !== address.expectedActorId ||
    !Object.entries(address.conversation).every(
      ([key, value]) => result.conversation?.[key as keyof LiveScope["conversation"]] === value,
    ) ||
    !intent ||
    intent.requestKey !== requestKey ||
    !validLiveAgentReference(intent.agent) ||
    intent.agent.agentKey !== agent.agentKey ||
    intent.agent.revision !== agent.revision ||
    intent.agent.mode !== agent.mode ||
    intent.agent.activationRevision !== agent.activationRevision ||
    !Number.isSafeInteger(intent.createdAt) ||
    !Number.isSafeInteger(intent.expiresAt) ||
    intent.createdAt > now ||
    intent.createdAt < now - 120_000 ||
    intent.expiresAt <= now ||
    intent.expiresAt - intent.createdAt > 120_000
  )
    throw new Error("The voice agent review did not match the saved voice agent and conversation.");
  await owner.quote(intent.agent);
  if (owner.snapshot().phase !== "quoted")
    throw new Error(owner.snapshot().error ?? "The voice agent price was not confirmed.");
}

export function useLiveTalk(
  scope: LiveScope | null,
  disabledReason: string | null = null,
  activities?: ReadonlyArray<OrchestrationThreadActivity>,
) {
  const owner = sharedController();
  const scopeKey = JSON.stringify(scope);
  useLayoutEffect(() => {
    owner.setScope(scope);
  }, [owner, scopeKey]);
  useEffect(() => () => owner.setScope(null), [owner]);
  const state = useSyncExternalStore(owner.subscribe, owner.snapshot, owner.snapshot);
  useLiveVoicePresentation(scope, activities, state.phase, owner.quote, readLivePresentation);
  return {
    ...state,
    disabledReason,
    available: scope !== null,
    quoteCall: owner.quote,
    setPrivacy: owner.setPrivacy,
    retryPrivacy: owner.retryPrivacy,
    reviewPrivacy: owner.reviewPrivacy,
    setVoice: owner.setVoice,
    retryVoice: owner.retryVoice,
    setAgreedMaximumSeconds: owner.setAgreedMaximumSeconds,
    start: owner.start,
    end: owner.end,
    endAndSettle: owner.endAndSettle,
    subscribeConversation: owner.subscribeConversation,
    isCurrentCall: owner.isCurrentCall,
    currentCallKey: owner.currentCallKey,
    sendUpdate: owner.sendUpdate,
  };
}
export type LiveTalkControls = ReturnType<typeof useLiveTalk>;
const usd = (micros: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  }).format(micros / 1_000_000);
const labels: Record<LiveState["phase"], string> = {
  off: "",
  quoting: "Checking Live availability and price…",
  quoted: "Review the price before enabling your microphone.",
  starting: "Preparing microphone and Live audio…",
  connecting: "Connecting Live audio…",
  live: "Live audio connected. Speak naturally.",
  reconnecting:
    "Live audio dropped. Ending the dropped call and reconnecting once with your conversation so far…",
  stopping: "Microphone stopped. Confirming the call has ended…",
  unconfirmed: "Microphone stopped. Final status and charge are unconfirmed.",
  ended: "Live ended.",
  error: "Live could not start.",
};
const ACTIVE_PHASES: ReadonlySet<LiveState["phase"]> = new Set([
  "starting",
  "connecting",
  "live",
  "reconnecting",
  "stopping",
  "unconfirmed",
]);
export function LiveTalkPanel({
  controls,
  taskNotice,
}: {
  controls: LiveTalkControls;
  taskNotice?: string | null;
}) {
  const agent = controls.call?.agent ?? controls.quote?.agent;
  const privacy = controls.call ? controls.call.privacy : controls.quote?.privacy;
  const providerRecording = validLiveProviderStorage(controls.call?.providerStorage)
    ? controls.call.providerStorage.observed
    : "unknown";
  const voices = liveVoiceChoices(controls.quote);
  const voice =
    controls.voicePending ??
    controls.call?.voice ??
    agent?.voice ??
    controls.quote?.voice ??
    LIVE_DEFAULT_VOICE;
  const saving =
    controls.privacySaving || controls.privacyRetry || controls.voiceSaving || controls.voiceRetry;
  const funded = controls.call?.maximumSeconds ?? controls.fundedSeconds;
  const extensions = controls.call?.extensions ?? controls.extensions;
  const showFunding = controls.call !== null || ACTIVE_PHASES.has(controls.phase);
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (controls.phase === "quoted" && agent) panel.current?.focus();
  }, [controls.phase, agent?.agentKey, agent?.revision]);
  if (controls.phase === "off") return null;
  return (
    <section
      ref={panel}
      tabIndex={-1}
      aria-label="Live voice controls"
      data-testid="live-panel"
      data-phase={controls.phase}
      className="mx-2 mb-2 rounded-xl border border-border/60 bg-muted/30 p-2.5"
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-medium">
          {agent
            ? `${agent.mode === "test" ? "Private test" : "Active agent"} · ${agent.name} · revision ${agent.revision}`
            : "Live · Private pilot"}
          <span className="ml-2 font-normal text-muted-foreground">
            Microphone <span data-testid="live-device-state">{controls.device}</span>
          </span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          data-testid="live-end"
          onClick={controls.end}
        >
          {controls.phase === "unconfirmed" ? "Retry End" : "End Live"}
        </Button>
      </div>
      <p role="status" data-testid="live-status" className="mt-1 text-xs text-muted-foreground">
        {labels[controls.phase]}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Audio goes to OpenAI. Spoken requests use this conversation’s selected model and context.
        Backend AI usage is charged separately. End Live stops audio; ongoing tasks remain in the
        chat.
      </p>
      <details className="mt-1 text-xs text-muted-foreground">
        <summary className="cursor-pointer">
          {privacy
            ? "Recording requested off · What is saved"
            : controls.call
              ? "What is saved · Recording policy unavailable"
              : "Privacy choice not yet verified"}
        </summary>
        <p className="mt-1">
          {privacy
            ? "No Live audio recording is requested. "
            : controls.call
              ? "This historical call has no retained recording policy. "
              : "The privacy choice has not yet been verified. "}
          {privacy?.taskContext === "spoken-request-only"
            ? "All your new speech since the previous task handoff is sent to the task model, including casual conversation. Earlier dialogue and Cleo’s spoken replies are omitted. Before the first handoff, this includes all your speech so far. "
            : privacy?.taskContext === "supporting-dialogue"
              ? "Your new speech and earlier supporting voice dialogue, including Cleo’s spoken replies, are sent to the task model. "
              : "The call’s task-context choice is unavailable. "}
          Both sides of the call are written into this conversation as messages, and sent messages
          remain in ordinary chat history. Call timing and charges remain in Usage. OpenAI’s
          separate data retention rules still apply.
        </p>
        {controls.call && (
          <p className="mt-1">
            Provider report:{" "}
            {providerRecording === "off"
              ? "recording off"
              : providerRecording === "on"
                ? "recording on"
                : providerRecording === "conflicting"
                  ? "conflicting reports"
                  : "not reported"}
            .
          </p>
        )}
        <a
          className="underline"
          href="https://developers.openai.com/api/docs/guides/your-data#v1livesessions"
          target="_blank"
          rel="noreferrer"
        >
          OpenAI data controls
        </a>
      </details>
      {controls.phase === "quoted" && privacy && (
        <fieldset disabled={saving} className="mt-2 text-xs">
          <label>
            Task context for future calls
            <select
              aria-label="Task context for future calls"
              className="ml-2 rounded border border-border bg-background px-2 py-1"
              value={controls.privacyPending ?? privacy.taskContext}
              onChange={(event) =>
                void controls.setPrivacy(event.target.value as LivePrivacy["taskContext"])
              }
            >
              <option value="spoken-request-only">New speech only</option>
              <option value="supporting-dialogue">New speech and supporting dialogue</option>
            </select>
          </label>
          <p className="mt-1 text-muted-foreground">
            Saved for you in this World. Changes apply to future calls and require a fresh price
            review. Recording stays off.
          </p>
        </fieldset>
      )}
      {controls.phase === "quoted" && (
        <fieldset disabled={saving} className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          {agent ? (
            <span>
              Voice <span data-testid="live-voice">{agent.voice}</span> (from the saved agent)
            </span>
          ) : (
            <label>
              Voice
              <select
                aria-label="Voice"
                data-testid="live-voice-select"
                className="ml-2 rounded border border-border bg-background px-2 py-1"
                value={voice}
                onChange={(event) => void controls.setVoice(event.target.value)}
              >
                {voices.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Agreed limit
            <select
              aria-label="Agreed limit"
              data-testid="live-limit-select"
              className="ml-2 rounded border border-border bg-background px-2 py-1"
              value={String(controls.agreedMaximumSeconds)}
              onChange={(event) =>
                void controls.setAgreedMaximumSeconds(Number(event.target.value))
              }
            >
              {LIVE_LIMIT_CHOICES.map((seconds) => (
                <option key={seconds} value={String(seconds)}>
                  {seconds} seconds
                </option>
              ))}
            </select>
          </label>
          <span className="text-muted-foreground">
            Funded a minute at a time up to your agreed limit; Live ends there.
          </span>
        </fieldset>
      )}
      {controls.privacySaving && (
        <p role="status" className="mt-1 text-xs">
          Saving privacy choice and refreshing the review…
        </p>
      )}
      {controls.voiceSaving && (
        <p role="status" className="mt-1 text-xs">
          Saving voice choice and refreshing the review…
        </p>
      )}
      {controls.privacyRetry && (
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={() => void controls.retryPrivacy()}
        >
          Retry same privacy choice
        </Button>
      )}
      {controls.voiceRetry && (
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={() => void controls.retryVoice()}
        >
          Retry same voice choice
        </Button>
      )}
      {(controls.privacyRetry || controls.voiceRetry) && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          onClick={() => void controls.reviewPrivacy()}
        >
          Review current choice
        </Button>
      )}
      {agent ? (
        <p className="mt-1 text-xs text-muted-foreground">
          This saved revision supplies the purpose, style and voice. Each spoken request uses your
          current conversation context and tools, including changes you make during the{" "}
          {agent.mode === "test" ? "test" : "call"}.
        </p>
      ) : null}
      {taskNotice ? (
        <p role="status" className="mt-1 text-xs">
          {taskNotice}
        </p>
      ) : null}
      {controls.quote && (controls.phase === "quoted" || controls.phase === "starting") ? (
        <p data-testid="live-price" className="mt-1 text-xs">
          {usd(controls.quote.price.rateMicrosPerMinute)} per minute · Maximum{" "}
          {usd(controls.quote.maximumVoiceChargeMicros)} for {controls.agreedMaximumSeconds}{" "}
          seconds. Unused voice credit is released after final usage is confirmed.
        </p>
      ) : null}
      {controls.quote?.availableCreditMicros !== undefined ? (
        <p data-testid="live-budget" className="mt-1 text-xs text-muted-foreground">
          Available credit {usd(controls.quote.availableCreditMicros)}
          {controls.quote.availableCreditMicros < controls.quote.maximumVoiceChargeMicros
            ? " · below the maximum for this limit; the World decides admission"
            : ""}
        </p>
      ) : null}
      {showFunding ? (
        <p data-testid="live-funding" className="mt-1 text-xs text-muted-foreground">
          Funded {funded ?? "…"} s of {controls.agreedMaximumSeconds} s agreed · Extensions{" "}
          <span data-testid="live-extension-count">{extensions}</span>
          {controls.deadlineAt !== null
            ? ` · Funded until ${new Date(controls.deadlineAt).toLocaleTimeString()}`
            : ""}
        </p>
      ) : null}
      {controls.call ? (
        <p data-testid="live-call" className="mt-1 text-xs text-muted-foreground">
          {controls.call.status === "settled" || controls.call.status === "released"
            ? `Voice charge ${usd(controls.call.chargeMicros)} · Released ${usd(controls.call.releasedMicros)}`
            : `Reserved up to ${usd(controls.call.maximumChargeMicros)}`}
          {" · Voice "}
          <span data-testid="live-call-voice">{controls.call.voice}</span>
          {" · Call "}
          <span data-testid="live-call-request-key">{controls.call.requestKey}</span>
        </p>
      ) : null}
      {controls.replacementOf ? (
        <p data-testid="live-replacement" className="mt-1 text-xs text-muted-foreground">
          Reconnected after a dropped connection. The earlier call{" "}
          <span data-testid="live-replaced-request-key">{controls.replacementOf}</span> was ended
          and this call continues its dialogue; work already sent was not repeated.
        </p>
      ) : null}
      {controls.extensionRefusal ? (
        <p role="status" data-testid="live-extension-refusal" className="mt-1 text-xs">
          {controls.extensionRefusal}
        </p>
      ) : null}
      {controls.refusal ? (
        <p role="alert" data-testid="live-refusal" className="mt-1 text-xs text-destructive">
          {controls.refusal}
        </p>
      ) : null}
      {controls.error && controls.error !== controls.refusal ? (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {controls.error}
        </p>
      ) : null}
      {controls.phase === "quoted" ? (
        <Button
          type="button"
          size="xs"
          className="mt-2"
          data-testid="live-accept"
          disabled={!controls.available || saving}
          onClick={() => void controls.start()}
        >
          Accept price and start microphone
        </Button>
      ) : null}
    </section>
  );
}
